/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-underscore-dangle */
import axios, { Axios, AxiosAdapter, AxiosRequestConfig } from 'axios';
import { authService } from 'services/api/auth';

import store from 'store';
import { userActions } from 'store/user';
import { trackRequest } from 'utils/session-activity';

export type ErrorObj = {
  code: number
  message: string
};
export interface IResponse {
  success: boolean
  errors?: Array<ErrorObj>
  total?: number;
}
export interface IErrorResponse {
  error?: number;
  errMessage?: string;
}

const URLS_EXPECT_401_WITHOUT_REFRESH_TOKEN = [
  '2fa/checkUserPassFor2Fa',
  'v1/auth/change-password',
];

/**
 * Флаг, показывающий, что токен обновляется
 */
let isRefreshingToken = false;
/**
 * Массив запросов, которые были вызваны в момент обновления токена
 * и ожидают повтора с новым токеном
 */
let refreshSubscribers: ((token: string | null) => void)[] = [];

/**
 * Вызывает коллбэки, которые ожидают обновления токена
 * @param token - новый токен
 */
function onRefreshed(token: string | null) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

const sendRequest = axios.defaults.adapter as AxiosAdapter;

const apiClient: Axios = axios.create({
  baseURL: '/panel-main/api',
  // Учёт на самой отправке: перехватчики ниже повторяют запросы и могут отказать до отправки
  adapter: (config) => trackRequest(config.method, () => sendRequest(config)),
});

const authInterceptor = async (request: AxiosRequestConfig) => {
  const { token, tokenExpiresAt } = store.getState().user;

  const currentProjectTag = store.getState().user.currentProject?.tag;
  // Регион на сервере общий для всех вкладок, поэтому передаём регион текущей вкладки явно
  const currentRegionId = store.getState().user.region?.id;

  if (currentProjectTag) {
    request.headers = {
      ...request.headers,
      'X-Project-Tag': currentProjectTag,
    };
  }

  if (currentRegionId) {
    request.headers = {
      ...request.headers,
      'X-Region-Id': String(currentRegionId),
    };
  }

  if (token && tokenExpiresAt && new Date(tokenExpiresAt).valueOf() < new Date().valueOf() + 3000) {
    if (!isRefreshingToken) {
      isRefreshingToken = true;
      try {
        const res = await authService.refreshToken();

        store.dispatch(userActions.setToken(res.data));
        request.headers!.Authorization = `Bearer ${res.data.data.accessToken}`;
        onRefreshed(res.data.data.accessToken);
      } catch (err) {
        onRefreshed(null);
        store.dispatch(userActions.clearUser());
        return await Promise.reject(err);
      } finally {
        isRefreshingToken = false;
      }
    } else {
      return new Promise((resolve) => {
        refreshSubscribers.push((newToken) => {
          if (newToken) {
            request.headers = {
              ...request.headers,
              Authorization: `Bearer ${newToken}`,
            };
            resolve(request);
          } else {
            resolve(Promise.reject(new Error('Failed to refresh token')));
          }
        });
      });
    }
  }
  if (token) {
    request.headers!.Authorization = `Bearer ${token}`;
  }
  return request;
};

function setAuthInterceptors(client: Axios) {
  client.interceptors.request.use(authInterceptor);
  client.interceptors.response.use(((res) => {
    const remoteVersion = res.headers['frontend-version']
      ? res.headers['frontend-version'] : undefined;
    const localVersion = localStorage.getItem('pdv');
    if (remoteVersion) {
      localStorage.setItem('pdv', remoteVersion);
      if (localVersion !== remoteVersion) {
        window.location.reload();
      }
    }
    return res;
  }), async (error) => {
    const originalRequest = error.config;
    const { response } = error;

    if (response
      && response.status === 401
      && !originalRequest._retry
      && !URLS_EXPECT_401_WITHOUT_REFRESH_TOKEN.includes(originalRequest.url)) {
      if (isRefreshingToken) {
        return new Promise((resolve) => {
          refreshSubscribers.push((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(client.request(originalRequest));
          });
        });
      }
      // eslint-disable-next-line no-underscore-dangle
      originalRequest._retry = true;
      isRefreshingToken = true;

      return authService.refreshToken()
        .then((res) => {
          if (res.data.data.accessToken) {
            store.dispatch(userActions.setToken(res.data));
            onRefreshed(res.data.data.accessToken);

            originalRequest.headers.Authorization = `Bearer ${res.data.data.accessToken}`;
            return client.request(originalRequest);
          }
          return Promise.reject(error);
        })
        .catch(() => {
          store.dispatch(userActions.clearUser());
          onRefreshed(null);
        })
        .finally(() => {
          isRefreshingToken = false;
        });
    }
    return Promise.reject(error);
  });
}

setAuthInterceptors(apiClient);

export default apiClient;
